import React, { useEffect, useMemo, useRef, useState } from "react";
import { Shot, RateCard, ProjectDocument, ProjectSetup } from "../types";
import type { Scene } from "../models/production";
import { X, Image as ImageIcon, ChevronLeft, ChevronRight, FileText, PanelRightClose, PanelRightOpen, MapPin, Users, Clapperboard, MoonStar, ChevronDown } from "lucide-react";
import { cn, formatCurrency, getEpisodeFromShotCode, getSequenceFromShotCode } from "../utils";
import { BID_ITEM_STATUSES, getBidStatusValue } from "../lib/bidStatus";

interface Props {
  shot: Shot;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (id: string, field: keyof Shot, value: any) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  readOnly?: boolean;
  rates: RateCard;
  setup: ProjectSetup;
  documents?: ProjectDocument[];
  scenes?: Scene[];
}

function parseEpisodeNumber(value?: string) {
  if (!value) return undefined;
  const match = value.match(/(\d+)$/);
  const parsed = Number.parseInt(match ? match[1] : value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function parseSceneIndexedEntry(entry: unknown) {
  const value = String(entry || "").trim();
  if (!value) return null;
  const matched = value.match(/^([^:]+):\s*(.*)$/);
  return {
    sceneNumber: matched?.[1]?.trim() || "",
    value: matched?.[2]?.trim() || value,
  };
}

function normalizeSceneKey(value?: string) {
  if (!value) return "";
  const trimmed = value.trim().toUpperCase().replace(/^SCENE\s+/, "");
  const matched = trimmed.match(/^0*(\d+)([A-Z]?)$/);
  if (!matched) return trimmed;
  return `${Number.parseInt(matched[1], 10)}${matched[2] || ""}`;
}

function findSceneIndexedEntry(entries: unknown, targetKeys: string[]) {
  if (!Array.isArray(entries) || targetKeys.length === 0) return null;
  const normalizedKeys = targetKeys.map(normalizeSceneKey).filter(Boolean);
  for (const entry of entries) {
    const parsed = parseSceneIndexedEntry(entry);
    if (!parsed) continue;
    if (normalizedKeys.includes(normalizeSceneKey(parsed.sceneNumber))) {
      return parsed;
    }
  }
  return null;
}

function findStructuredScriptScene(scenes: unknown, targetKeys: string[]) {
  if (!Array.isArray(scenes) || targetKeys.length === 0) return null;
  const normalizedKeys = targetKeys.map(normalizeSceneKey).filter(Boolean);
  for (const scene of scenes) {
    if (!scene || typeof scene !== "object") continue;
    const sceneNumber = normalizeSceneKey(
      String((scene as any).sceneNumber || (scene as any).scene_number || "")
    );
    if (normalizedKeys.includes(sceneNumber)) {
      return scene as Record<string, unknown>;
    }
  }
  return null;
}

function extractSceneHeadingFromText(value?: string) {
  const firstLine = String(value || "").split("\n").map((line) => line.trim()).find(Boolean);
  if (!firstLine) return undefined;
  return /^(INT|EXT|INT\/EXT|I\/E)\.?/i.test(firstLine) ? firstLine : undefined;
}

function stripLeadingHeading(value: string | undefined, heading: string | undefined) {
  const text = String(value || "").trim();
  const title = String(heading || "").trim();
  if (!text) return undefined;
  if (!title) return text;
  if (text === title) return undefined;
  if (text.startsWith(title)) {
    return text.slice(title.length).replace(/^\s+/, "").trim() || undefined;
  }
  return text;
}

function isCharacterCue(line: string) {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length > 40) return false;
  if (/[a-z]/.test(trimmed)) return false;
  return /^[A-Z0-9' .()\-]+$/.test(trimmed);
}

function renderScriptExcerpt(excerpt: string) {
  const lines = excerpt.split("\n");
  const nodes: React.ReactNode[] = [];
  let dialogueBuffer: string[] = [];
  let afterCue = false;
  let index = 0;

  const flushDialogue = () => {
    if (!dialogueBuffer.length) return;
    nodes.push(
      <div key={`dialogue-${index++}`} className="ml-[86px] mt-2 max-w-[280px] whitespace-pre-wrap text-[12px] leading-[1.22]">
        {dialogueBuffer.join("\n")}
      </div>
    );
    dialogueBuffer = [];
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    if (!trimmed) {
      flushDialogue();
      afterCue = false;
      nodes.push(<div key={`space-${index++}`} className="h-3" />);
      continue;
    }

    if (isCharacterCue(trimmed)) {
      flushDialogue();
      afterCue = true;
      nodes.push(
        <div key={`cue-${index++}`} className="ml-[118px] mt-5 max-w-[220px] text-center text-[12px] uppercase leading-[1.1]">
          {trimmed}
        </div>
      );
      continue;
    }

    if (afterCue) {
      dialogueBuffer.push(trimmed);
      continue;
    }

    flushDialogue();
    nodes.push(
      <div key={`action-${index++}`} className="whitespace-pre-wrap text-[12px] leading-[1.28]">
        {trimmed}
      </div>
    );
  }

  flushDialogue();

  return nodes;
}

export function ShotDetailModal({ shot, isOpen, onClose, onUpdate, onNavigate, hasPrev, hasNext, readOnly, rates, setup, documents = [], scenes = [] }: Props) {
  const [showScriptPane, setShowScriptPane] = useState(true);
  const scriptScrollRef = useRef<HTMLDivElement | null>(null);
  const [showScriptScrollHint, setShowScriptScrollHint] = useState(false);

  const calculateShotTotal = (shot: Shot) => {
    const baseTaskCost =
      (shot.days.concept || 0) * (rates.concept || 0) +
      (shot.days.dmp || 0) * (rates.dmp || 0) +
      (shot.days.comp || 0) * (rates.comp || 0) +
      (shot.days.roto || 0) * (rates.roto || 0) +
      (shot.days.prep || 0) * (rates.prep || 0) +
      (shot.days.gfx || 0) * (rates.gfx || 0) +
      (shot.days.cameraTR || 0) * (rates.cameraTR || 0) +
      (shot.days.objectTR || 0) * (rates.objectTR || 0) +
      (shot.days.rANIM || 0) * (rates.rANIM || 0) +
      (shot.days.layout || 0) * (rates.layout || 0) +
      (shot.days.anim || 0) * (rates.anim || 0) +
      (shot.days.light || 0) * (rates.light || 0) +
      (shot.days.fx || 0) * (rates.fx || 0);
      
    const taskDays = 
      (shot.days.concept || 0) +
      (shot.days.dmp || 0) +
      (shot.days.comp || 0) +
      (shot.days.roto || 0) +
      (shot.days.prep || 0) +
      (shot.days.gfx || 0) +
      (shot.days.cameraTR || 0) +
      (shot.days.objectTR || 0) +
      (shot.days.rANIM || 0) +
      (shot.days.layout || 0) +
      (shot.days.anim || 0) +
      (shot.days.light || 0) +
      (shot.days.fx || 0);
    
    const supDays = taskDays * ((setup.supervisionPercentage || 0) / 100);
    const supCost = supDays * (rates.supervisor || 0);
    const taskCost = baseTaskCost + supCost;
    return taskCost * (shot.quantity || 1);
  };

  const shotTotal = calculateShotTotal(shot);
  const episodeCode = getEpisodeFromShotCode(shot.shotCode);
  const sequenceCode = getSequenceFromShotCode(shot.shotCode);

  const scriptDoc = useMemo(() => {
    const shotEpisodeNumber = parseEpisodeNumber(episodeCode);
    return documents.find((doc) => {
      if (!doc.episode) return false;
      const documentEpisodeNumber = parseEpisodeNumber(doc.episode);
      return shotEpisodeNumber !== undefined && documentEpisodeNumber === shotEpisodeNumber;
    });
  }, [documents, episodeCode]);

  const scriptContext = useMemo(() => {
    const scriptData = scriptDoc?.analysisResult?.script_data || {};
    const sequenceNumber = Number.parseInt(sequenceCode, 10);
    const sceneLookupKeys = [
      sequenceCode,
      Number.isFinite(sequenceNumber) ? String(sequenceNumber) : undefined,
    ].filter((value): value is string => Boolean(value));

    const persistedScene = scenes.find((scene) => {
      if (shot.sceneId && scene.id === shot.sceneId) return true;
      if (shot.sequenceId && scene.sequenceId === shot.sequenceId) return true;
      return sceneLookupKeys.includes(String(scene.sceneNumber || ""));
    });

    const structuredScene = persistedScene || findStructuredScriptScene(scriptData.scenes, sceneLookupKeys);
    const parsedAnalysis = findSceneIndexedEntry(scriptData.scene_analysis, sceneLookupKeys);
    const parsedCharacters = findSceneIndexedEntry(scriptData.characters_by_scene, sceneLookupKeys);
    const headingText =
      String(structuredScene?.heading || "").trim() ||
      parsedAnalysis?.value ||
      undefined;
    const characterList = Array.isArray((structuredScene as any)?.characters) && (structuredScene as any).characters.length
      ? (structuredScene as any).characters.map((item: string) => item.trim()).filter(Boolean)
      : parsedCharacters?.value
        ? parsedCharacters.value.split(",").map((item) => item.trim()).filter(Boolean)
        : [];
    const rawStructuredScriptText =
      structuredScene && typeof structuredScene === "object"
        ? ((structuredScene as any).scriptText || (structuredScene as any).script_text || "")
        : "";
    const verbatimScriptText = stripLeadingHeading(
      String(rawStructuredScriptText).trim(),
      headingText
    );

    return {
      sceneHeading: headingText,
      location: headingText,
      characters: characterList,
      scriptExcerpt: verbatimScriptText,
      sceneNumberLabel: String((structuredScene as any)?.sceneNumber || (structuredScene as any)?.scene_number || parsedAnalysis?.sceneNumber || sequenceCode),
    };
  }, [scriptDoc, sequenceCode, scenes, shot.sceneId, shot.sequenceId]);

  const detailMeta = [
    {
      label: "Episode / Sequence",
      value: episodeCode ? `Ep ${episodeCode} / Seq ${sequenceCode}` : `Seq ${sequenceCode}`,
      icon: Clapperboard,
    },
    {
      label: "Scene Description",
      value: shot.sceneDescription,
      icon: FileText,
    },
    {
      label: "Location",
      value: scriptContext.location,
      icon: MapPin,
    },
    {
      label: "Characters",
      value: scriptContext.characters.length ? scriptContext.characters.join(", ") : undefined,
      icon: Users,
    },
    {
      label: "Time",
      value: scriptContext.sceneHeading?.match(/\b(DAY|NIGHT|DUSK|DAWN)\b/i)?.[0]?.toUpperCase(),
      icon: MoonStar,
    },
  ].filter((item) => item.value);

  useEffect(() => {
    const node = scriptScrollRef.current;
    if (!node || !showScriptPane) {
      setShowScriptScrollHint(false);
      return;
    }

    const updateHint = () => {
      const hasMore = node.scrollHeight - node.scrollTop - node.clientHeight > 12;
      setShowScriptScrollHint(hasMore);
    };

    updateHint();
    node.addEventListener("scroll", updateHint);
    window.addEventListener("resize", updateHint);
    return () => {
      node.removeEventListener("scroll", updateHint);
      window.removeEventListener("resize", updateHint);
    };
  }, [showScriptPane, scriptContext.scriptExcerpt, scriptContext.sceneHeading]);

  if (!isOpen) return null;

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, field: keyof Shot, currentValue: string) => {
    if (!onUpdate) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.target as HTMLTextAreaElement;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      
      if (currentValue.length === 0) {
        const bullet = "• ";
        onUpdate(shot.id, field, bullet);
        setTimeout(() => {
          target.selectionStart = target.selectionEnd = bullet.length;
        }, 0);
        return;
      }

      const bullet = "\n• ";
      const newValue = currentValue.substring(0, start) + bullet + currentValue.substring(end);
      onUpdate(shot.id, field, newValue);
      
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + bullet.length;
      }, 0);
    }
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly || !onUpdate) return;
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const targetWidth = 480;
          const targetHeight = 270;
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");

          if (ctx) {
            const imgAspectRatio = img.width / img.height;
            const targetAspectRatio = targetWidth / targetHeight;
            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgAspectRatio > targetAspectRatio) {
              drawHeight = targetHeight;
              drawWidth = img.width * (targetHeight / img.height);
              offsetX = (targetWidth - drawWidth) / 2;
              offsetY = 0;
            } else {
              drawWidth = targetWidth;
              drawHeight = img.height * (targetWidth / img.width);
              offsetX = 0;
              offsetY = (targetHeight - drawHeight) / 2;
            }

            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
            const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            onUpdate(shot.id, "thumbnail", dataUrl);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50/70">
          <div className="flex min-w-0 items-center gap-3">
            <h2 className="text-[18px] font-bold text-slate-800 truncate">{shot.shotCode}</h2>
            <div className="h-5 w-px bg-slate-200 shrink-0"></div>
            <div className="text-[18px] font-bold text-indigo-600 shrink-0">
              {formatCurrency(shotTotal, setup.currency)}
            </div>
            <p className="text-xs text-slate-500 shrink-0">Shot Details</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onNavigate && (
              <div className="flex items-center gap-1 border-r pr-3 border-slate-200 mr-1">
                <button
                  onClick={() => onNavigate('prev')}
                  disabled={!hasPrev}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onNavigate('next')}
                  disabled={!hasNext}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            <button
              onClick={() => setShowScriptPane((prev) => !prev)}
              className="inline-flex w-[118px] items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              {showScriptPane ? <PanelRightClose className="w-3.5 h-3.5" /> : <PanelRightOpen className="w-3.5 h-3.5" />}
              {showScriptPane ? "Hide Script" : "Show Script"}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-5 pt-4 border-b border-slate-100 bg-white">
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-max items-start gap-2">
            {[
              { key: "supervisor", label: "Sup" },
              { key: "concept", label: "Concept" },
              { key: "dmp", label: "DMP" },
              { key: "comp", label: "Comp" },
              { key: "roto", label: "Roto" },
              { key: "prep", label: "Prep" },
              { key: "gfx", label: "GFX" },
              { key: "cameraTR", label: "CamTR" },
              { key: "objectTR", label: "ObjTR" },
              { key: "rANIM", label: "rANIM" },
              { key: "layout", label: "Layout" },
              { key: "anim", label: "Anim" },
              { key: "light", label: "Light" },
              { key: "fx", label: "FX" },
            ].map((task) => {
              let displayValue: number | string = shot.days[task.key as keyof Shot['days']] || 0;
              let isReadOnly = readOnly;

              if (task.key === "supervisor") {
                const otherDays =
                  (shot.days.concept || 0) +
                  (shot.days.dmp || 0) +
                  (shot.days.comp || 0) +
                  (shot.days.roto || 0) +
                  (shot.days.prep || 0) +
                  (shot.days.gfx || 0) +
                  (shot.days.cameraTR || 0) +
                  (shot.days.objectTR || 0) +
                  (shot.days.rANIM || 0) +
                  (shot.days.layout || 0) +
                  (shot.days.anim || 0) +
                  (shot.days.light || 0) +
                  (shot.days.fx || 0);

                displayValue = (otherDays * ((setup.supervisionPercentage || 0) / 100)).toFixed(2);
                if (String(displayValue).endsWith(".00")) displayValue = String(displayValue).slice(0, -3);
                isReadOnly = true;
              }

              return (
                <div key={task.key} className="w-[76px] shrink-0 space-y-0.5">
                  <label className="block text-[8.5px] font-semibold text-slate-500 uppercase tracking-[0.12em]">{task.label}</label>
                  {onUpdate && !isReadOnly ? (
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={displayValue || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        onUpdate(shot.id, "days", { ...shot.days, [task.key]: val === "" ? 0 : parseFloat(val) || 0 });
                      }}
                      className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                    />
                  ) : (
                    <div className="w-full px-2 py-1 text-[11px] border border-slate-200 rounded-md bg-slate-50 text-slate-800 font-medium">
                      {displayValue || 0}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </div>
          <div className="flex flex-wrap items-start gap-2 mt-3 pb-4">
            {detailMeta.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className={cn(
                    "min-h-[84px] p-3 bg-indigo-50/40 rounded-xl border border-indigo-100 flex flex-col",
                    item.label === "Episode / Sequence" && "min-w-[124px] max-w-[156px] flex-[0.65]",
                    item.label === "Scene Description" && "min-w-[260px] flex-[1.45]",
                    item.label === "Time" && "min-w-[82px] max-w-[96px] flex-[0.3]",
                    item.label !== "Episode / Sequence" && item.label !== "Scene Description" && item.label !== "Time" && "min-w-[170px] flex-1"
                  )}
                >
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <Icon className="w-3 h-3 text-indigo-500" />
                    {item.label}
                  </p>
                  <div className="flex-1 text-[12px] text-slate-700 leading-relaxed line-clamp-2">{item.value}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className={cn("min-h-full lg:h-full grid", showScriptPane ? "lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]" : "grid-cols-1")}>
            <div className="min-h-0 p-5 lg:overflow-y-auto">
              <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-5">
                <div className="space-y-4">
                  <div className={cn(
                    "relative aspect-video rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden group",
                shot.thumbnail && "border-none"
              )}>
                {shot.thumbnail ? (
                  <img 
                    src={shot.thumbnail} 
                    alt={shot.shotCode} 
                    className="w-full h-full object-contain bg-black"
                  />
                ) : (
                  <div className="text-center p-8">
                    <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm font-medium">No thumbnail available</p>
                  </div>
                )}
                {!readOnly && onUpdate && (
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <span className="text-white font-medium bg-black/50 px-4 py-2 rounded-lg backdrop-blur-sm">
                      Upload Thumbnail
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleThumbnailUpload}
                    />
                  </label>
                )}
              </div>

                  <div className="grid grid-cols-[minmax(0,1fr)_92px] gap-3">
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Status</p>
                      {onUpdate && !readOnly ? (
                        <select
                          value={getBidStatusValue(shot)}
                          onChange={(e) => onUpdate(shot.id, "status", e.target.value)}
                          className={cn(
                            "w-full px-2 py-1 text-[13px] font-bold border rounded focus:ring-2 focus:ring-indigo-500 outline-none transition-colors",
                            getBidStatusValue(shot) === "Bidding" && "bg-purple-50 text-purple-700 border-purple-200",
                            getBidStatusValue(shot) === "Off" && "bg-slate-50 text-slate-600 border-slate-200",
                            getBidStatusValue(shot) === "Lost" && "bg-rose-50 text-rose-700 border-rose-200",
                            getBidStatusValue(shot) === "Awarded" && "bg-emerald-50 text-emerald-700 border-emerald-200",
                            getBidStatusValue(shot) === "Rebid" && "bg-orange-50 text-orange-700 border-orange-200"
                          )}
                        >
                          {BID_ITEM_STATUSES.map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-[13px] font-bold text-slate-800">{getBidStatusValue(shot)}</p>
                      )}
                    </div>

                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Quantity</p>
                      {onUpdate && !readOnly ? (
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={shot.quantity || 1}
                          onChange={(e) => onUpdate(shot.id, "quantity", parseInt(e.target.value) || 1)}
                          className="w-full bg-white border border-slate-200 px-2 py-1 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-[13px] font-bold text-slate-800"
                        />
                      ) : (
                        <p className="text-[13px] font-bold text-slate-800">{shot.quantity || 1}</p>
                      )}
                    </div>
                  </div>

                </div>

                <div className="space-y-4">
              <div>
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Description</h3>
                {onUpdate && !readOnly ? (
                  <textarea
                    value={shot.description || ""}
                    onChange={(e) => onUpdate(shot.id, "description", e.target.value)}
                    onKeyDown={(e) => handleTextareaKeyDown(e, "description", shot.description || "")}
                    className="w-full bg-white p-3 rounded-xl border border-slate-200 min-h-[84px] text-slate-700 text-[13px] leading-relaxed focus:ring-2 focus:ring-indigo-500 outline-none resize-y"
                    placeholder="No description provided"
                  />
                ) : (
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 min-h-[84px] whitespace-pre-wrap text-slate-700 text-[13px] leading-relaxed">
                    {shot.description || <span className="text-slate-300 italic">No description provided</span>}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Scope of Work (Client Description)</h3>
                {onUpdate && !readOnly ? (
                  <textarea
                    value={shot.scopeOfWork || ""}
                    onChange={(e) => onUpdate(shot.id, "scopeOfWork", e.target.value)}
                    onKeyDown={(e) => handleTextareaKeyDown(e, "scopeOfWork", shot.scopeOfWork || "")}
                    className="w-full bg-white p-3 rounded-xl border border-slate-200 min-h-[84px] text-slate-700 text-[13px] leading-relaxed focus:ring-2 focus:ring-indigo-500 outline-none resize-y"
                    placeholder="No scope of work provided"
                  />
                ) : (
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 min-h-[84px] whitespace-pre-wrap text-slate-700 text-[13px] leading-relaxed">
                    {shot.scopeOfWork || <span className="text-slate-300 italic">No scope of work provided</span>}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Assumptions</h3>
                  {onUpdate && !readOnly ? (
                    <textarea
                      value={shot.assumptions || ""}
                      onChange={(e) => onUpdate(shot.id, "assumptions", e.target.value)}
                      onKeyDown={(e) => handleTextareaKeyDown(e, "assumptions", shot.assumptions || "")}
                      className="w-full bg-white p-3 rounded-xl border border-slate-200 min-h-[72px] text-slate-600 text-[13px] leading-relaxed focus:ring-2 focus:ring-indigo-500 outline-none resize-y"
                      placeholder="No assumptions provided"
                    />
                  ) : (
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 min-h-[72px] whitespace-pre-wrap text-slate-600 text-[13px] leading-relaxed">
                      {shot.assumptions || <span className="text-slate-300 italic">No assumptions provided</span>}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Internal Notes</h3>
                  {onUpdate && !readOnly ? (
                    <textarea
                      value={shot.internalNotes || ""}
                      onChange={(e) => onUpdate(shot.id, "internalNotes", e.target.value)}
                      onKeyDown={(e) => handleTextareaKeyDown(e, "internalNotes", shot.internalNotes || "")}
                      className="w-full bg-white p-3 rounded-xl border border-slate-200 min-h-[72px] text-slate-600 text-[13px] leading-relaxed italic focus:ring-2 focus:ring-indigo-500 outline-none resize-y"
                      placeholder="No internal notes"
                    />
                  ) : (
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 min-h-[72px] whitespace-pre-wrap text-slate-600 text-[13px] leading-relaxed italic">
                      {shot.internalNotes || <span className="text-slate-300 italic">No internal notes</span>}
                    </div>
                  )}
                </div>
              </div>
                  </div>
                </div>
            </div>

            {showScriptPane && (
              <aside className="hidden lg:flex min-h-0 h-full max-h-full flex-col overflow-hidden border-l border-slate-100 bg-white">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center">
                  <div className="inline-flex items-center gap-3">
                    <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Script Excerpt</h3>
                    <span className="h-3.5 w-px bg-slate-200" aria-hidden="true" />
                    <p className="text-[11px] font-medium text-slate-400">
                    {scriptContext.sceneNumberLabel ? `Scene ${scriptContext.sceneNumberLabel}` : `Sequence ${sequenceCode}`}
                    </p>
                  </div>
                </div>
                <div ref={scriptScrollRef} className="relative h-0 min-h-0 flex-1 overflow-y-scroll overscroll-contain px-8 py-7 pr-5">
                  <div className="space-y-7 pb-6 text-slate-900" style={{ fontFamily: '"Courier Prime","Courier New",monospace' }}>
                    <div className="grid grid-cols-[40px_minmax(0,1fr)_40px] items-start gap-4 text-[11px]">
                      <span className="pt-0.5">{scriptContext.sceneNumberLabel || sequenceCode}</span>
                      <div className="text-center text-[14px] font-bold uppercase tracking-[0.03em] underline decoration-[1.5px] underline-offset-[6px]">
                        {scriptContext.sceneHeading || shot.sceneDescription || "No script heading available"}
                      </div>
                      <span className="pt-0.5 text-right">{scriptContext.sceneNumberLabel || sequenceCode}</span>
                    </div>
                    <div className="border-t border-slate-100 pt-7 text-[12px] leading-[1.28]">
                      {scriptContext.scriptExcerpt ? renderScriptExcerpt(scriptContext.scriptExcerpt) : null}
                    </div>
                  </div>
                  {showScriptScrollHint && (
                    <button
                      type="button"
                      onClick={() => scriptScrollRef.current?.scrollBy({ top: 240, behavior: "smooth" })}
                      className="sticky bottom-3 ml-auto flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-500 shadow-sm hover:bg-slate-50 hover:text-slate-700"
                      title="Scroll script"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </aside>
            )}
          </div>
        </div>

        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
